import { getLanguage } from 'cdui-js';
import { fetchCountry } from '../../../api/common';

/** /sys/dict/country 单条：code=ISO，extra=区号（不带 +），文案按语言取 sc/tc/enDesc */
export type CrsCountryRow = {
  code: string;
  label: string;
  value: string;
  extra: string;
  other: string;
};

let rows: CrsCountryRow[] = [];
let grouped: Record<string, CrsCountryRow[]> = {};
let loading: Promise<void> | null = null;

const descKey = () => {
  const lang = String(getLanguage() || 'zh-CN');
  if (lang.startsWith('en')) return 'enDesc';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant') return 'tcDesc';
  return 'scDesc';
};

/** 兼容拦截器已拆 data，或整包 { code, message, data } */
const unwrapGroups = (raw: unknown): Record<string, unknown> => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const row = raw as Record<string, unknown>;
  const nested = row.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return row;
};

export const crsCountryGroups = () => grouped;

const matchRow = (raw: string) => {
  const value = raw.trim();
  if (!value) return undefined;
  const dial = value.replace(/^\+/, '');
  return rows.find(
    (item) => item.code === value || item.value === value || item.label === value || item.extra === dial,
  );
};

export const crsCountryLabel = (code: string) => matchRow(code)?.label || '';

/** 区号数字，供 /base/region/children 的 countryCode（+86 / 86）对齐 */
export const crsCountryDial = (code: string) =>
  String(rows.find((item) => item.code === code)?.extra || '')
    .replace(/^\+/, '')
    .trim();

/** 回填 / 选中：ISO、展示名或区号 → ISO code */
export const resolveCrsCountryCode = (raw: string) => {
  const value = raw.trim();
  if (!value) return '';
  return matchRow(value)?.code || value;
};

export const loadCrsCountryDict = () => {
  if (rows.length) return Promise.resolve();
  if (loading) return loading;
  loading = (async () => {
    const data = unwrapGroups(await fetchCountry());
    const key = descKey();
    const nextRows: CrsCountryRow[] = [];
    const nextGrouped: Record<string, CrsCountryRow[]> = {};
    Object.keys(data)
      .sort()
      .forEach((letter) => {
        const list = data[letter];
        if (!Array.isArray(list)) return;
        const mapped = list
          .map((item) => {
            const row = item as Record<string, unknown>;
            const code = String(row?.code ?? '').trim();
            if (!code) return null;
            const label = String(row?.[key] || row?.scDesc || row?.enDesc || code).trim();
            const extra = String(row?.extra ?? '')
              .replace(/^\+/, '')
              .trim();
            const mapped: CrsCountryRow = {
              code,
              label,
              value: code,
              extra,
              other: String(row?.other || letter),
            };
            return mapped;
          })
          .filter(Boolean) as CrsCountryRow[];
        if (!mapped.length) return;
        nextGrouped[letter] = mapped;
        nextRows.push(...mapped);
      });
    rows = nextRows;
    grouped = nextGrouped;
  })().finally(() => {
    loading = null;
  });
  return loading;
};
